'use client';

import React, { useState, useRef, useEffect } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import * as htmlToImage from 'html-to-image';
import { jsPDF } from 'jspdf';
import { useToast } from '../../components/ui/Toast';
import {
  ChevronDown, Download, Copy, Image as ImageIcon, Monitor, Smartphone,
  Mail, Phone, Globe, MapPin, FileText, Sparkles, X, Building2,
} from 'lucide-react';
import './BusinessCardGenerator.css';

/* ─── Static data ────────────────────────────────────────────── */
/* Each `defaultColor` is a suggested starting accent only — overridden
   by the merchant's own theme color on first use, or by whatever they
   later pick from the color picker. */
const TEMPLATES = [
  { id: 'boutique',   name: 'Boutique',          desc: 'Fashion, retail, lifestyle brands',     defaultColor: '#7c3aed' },
  { id: 'restaurant', name: 'Restaurant & Food', desc: 'Eateries, catering, food vendors',      defaultColor: '#ea580c' },
  { id: 'realestate', name: 'Real Estate',       desc: 'Agents, brokers, property managers',    defaultColor: '#0f172a' },
  { id: 'beauty',     name: 'Beauty & Wellness', desc: 'Salons, spas, wellness practitioners',  defaultColor: '#ec4899' },
  { id: 'trades',     name: 'Trades & Services', desc: 'Plumbing, electrical, repairs',         defaultColor: '#f59e0b' },
  { id: 'tech',       name: 'Tech & Consulting', desc: 'Freelancers, developers, consultants',  defaultColor: '#0d7463' },
  { id: 'creator',    name: 'Creator',           desc: 'Content creators, influencers, artists', defaultColor: '#e11d48' },
  { id: 'healthcare', name: 'Healthcare',        desc: 'Doctors, clinics, allied health',       defaultColor: '#0284c7' },
];

/* Migration map for merchants who already have a card saved under
   the old generic template IDs. Keeps stored cards working after
   the industry-template upgrade. */
const LEGACY_TEMPLATE_MAP = {
  minimal:   'tech',
  bold:      'creator',
  elegant:   'boutique',
  dark:      'realestate',
  creative:  'beauty',
  brutalist: 'trades',
};

const DEFAULT_TEMPLATE = 'boutique';

/* Templates whose visuals are dark/bold — QR foreground must be white
   for scannability. Used in both QR renders (front + back). */
const DARK_TEMPLATES = new Set(['realestate', 'creator']);

const COLOR_PRESETS = [
  { id: 'blue',    color: '#2563eb' },
  { id: 'emerald', color: '#059669' },
  { id: 'purple',  color: '#7c3aed' },
  { id: 'amber',   color: '#d97706' },
  { id: 'rose',    color: '#e11d48' },
  { id: 'slate',   color: '#475569' },
  { id: 'black',   color: '#000000' },
];

const BACK_PATTERNS = {
  boutique:   { type: 'marble',    name: 'Marble Texture' },
  restaurant: { type: 'marble',    name: 'Warm Marble' },
  realestate: { type: 'grid',      name: 'Dark Grid' },
  beauty:     { type: 'dots',      name: 'Soft Dots' },
  trades:     { type: 'stripes',   name: 'Bold Stripes' },
  tech:       { type: 'dots',      name: 'Subtle Dots' },
  creator:    { type: 'geometric', name: 'Geometric Shapes' },
  healthcare: { type: 'dots',      name: 'Calm Dots' },
};

const SOCIALS = [
  { id: 'linkedin',  name: 'LinkedIn',    icon: 'M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z' },
  { id: 'instagram', name: 'Instagram',   icon: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z' },
  { id: 'twitter',   name: 'X (Twitter)', icon: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z' },
  { id: 'tiktok',    name: 'TikTok',      icon: 'M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z' },
  { id: 'whatsapp',  name: 'WhatsApp',    icon: 'M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' },
];

const STORAGE_KEY = 'revstar_business_card';

const loadSavedData = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (!saved) return null;
    const parsed = JSON.parse(saved);
    // Migrate old generic template IDs to the industry-tuned IDs so
    // cards saved before the upgrade keep rendering.
    const oldId = parsed?.settings?.template;
    if (oldId && LEGACY_TEMPLATE_MAP[oldId]) {
      parsed.settings.template = LEGACY_TEMPLATE_MAP[oldId];
    }
    return parsed;
  } catch (e) {
    console.error('Failed to load saved business card:', e);
    return null;
  }
};

/* Collapsible form section — defined at MODULE scope, not inside the
   parent component, so its function reference is stable across
   re-renders. Defining it inside the parent makes React treat each
   render as a different component type, which unmounts + remounts the
   whole subtree on every keystroke and steals focus from inputs. */
function FormSection({ id, title, expanded, onToggle, children }) {
  return (
    <section className={`bcg-section${expanded ? ' is-expanded' : ''}`}>
      <button
        type="button"
        className="bcg-section-head"
        onClick={() => onToggle(id)}
        aria-expanded={expanded}
      >
        <h3 className="bcg-section-title">{title}</h3>
        <ChevronDown size={18} className="bcg-section-chevron" />
      </button>
      {expanded && <div className="bcg-section-body">{children}</div>}
    </section>
  );
}

export default function BusinessCardGenerator({ prefillData = null }) {
  const cardRef = useRef(null);
  const backRef = useRef(null);
  const toast = useToast();
  const isInitialMount = useRef(true);

  const savedData = useRef(loadSavedData());

  const [isFlipped, setIsFlipped] = useState(false);

  const [data, setData] = useState(() => {
    const saved = savedData.current;
    return {
      fullName:  saved?.data?.fullName  || prefillData?.accountName || prefillData?.businessName || '',
      title:     saved?.data?.title     || '',
      company:   saved?.data?.company   || prefillData?.businessName || '',
      email:     saved?.data?.email     || prefillData?.email || '',
      phone:     saved?.data?.phone     || prefillData?.phone || '',
      website:   saved?.data?.website   || prefillData?.website || '',
      location:  saved?.data?.location  || prefillData?.address || '',
      bio:       saved?.data?.bio       || '',
      headshot:  saved?.data?.headshot  || null,
      logo:      saved?.data?.logo      || prefillData?.logo || null,
      linkedin:  saved?.data?.linkedin  || '',
      instagram: saved?.data?.instagram || '',
      twitter:   saved?.data?.twitter   || '',
      tiktok:    saved?.data?.tiktok    || '',
      whatsapp:  saved?.data?.whatsapp  || prefillData?.whatsapp || '',
      ctaLabel:  saved?.data?.ctaLabel  || '',
      ctaUrl:    saved?.data?.ctaUrl    || '',
    };
  });

  const [template,    setTemplate]    = useState(() => savedData.current?.settings?.template    || DEFAULT_TEMPLATE);
  /* Accent priority: saved value → store theme color → brand teal. */
  const [accentColor, setAccentColor] = useState(() =>
    savedData.current?.settings?.accentColor ||
    prefillData?.themeAccentColor ||
    '#0d7463'
  );
  const [layout,      setLayout]      = useState(() => savedData.current?.settings?.layout      || 'landscape');
  const [previewMode, setPreviewMode] = useState('desktop');
  const [previewBg,   setPreviewBg]   = useState('light');
  const [showQR,      setShowQR]      = useState(() => savedData.current?.settings?.showQR || false);
  const [isExporting, setIsExporting] = useState(false);

  // Auto-save
  useEffect(() => {
    if (isInitialMount.current) { isInitialMount.current = false; return; }
    const saveData = {
      data,
      settings: { template, accentColor, layout, showQR },
      savedAt: Date.now(),
    };
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(saveData)); }
    catch (e) { console.error('Failed to save business card:', e); }
  }, [data, template, accentColor, layout, showQR]);

  // Collapsible sections
  const [expandedSections, setExpandedSections] = useState({
    template: true,
    color:    false,
    layout:   false,
    basic:    true,
    contact:  false,
    profile:  false,
    social:   false,
    cta:      false,
  });

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({ ...prev, [section]: !prev[section] }));
  };

  const updateData = (field, value) => {
    setData((prev) => ({ ...prev, [field]: value }));
  };

  const handleHeadshotUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => updateData('headshot', reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => updateData('logo', reader.result);
      reader.readAsDataURL(file);
    }
  };

  /* ── Exports ─────────────────────────────────────────────── */
  const downloadPNG = async () => {
    if (!cardRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, {
        quality: 1, pixelRatio: 3, cacheBust: true,
      });
      const link = document.createElement('a');
      link.href = dataUrl;
      link.download = `${data.fullName || 'business-card'}.png`;
      link.click();
      toast.success('PNG downloaded');
    } catch (err) { toast.error('Export failed'); }
    finally { setIsExporting(false); }
  };

  const downloadPDF = async () => {
    if (!cardRef.current || !backRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const frontUrl = await htmlToImage.toPng(cardRef.current, { quality: 1, pixelRatio: 3 });
      const backUrl  = await htmlToImage.toPng(backRef.current, { quality: 1, pixelRatio: 3 });
      const isPortrait = layout === 'portrait';
      const pdf = new jsPDF({
        orientation: isPortrait ? 'portrait' : 'landscape',
        unit: 'in',
        format: isPortrait ? [2.5, 4] : [3.5, 2],
      });
      pdf.addImage(frontUrl, 'PNG', 0, 0, isPortrait ? 2.5 : 3.5, isPortrait ? 4 : 2);
      pdf.addPage();
      pdf.addImage(backUrl,  'PNG', 0, 0, isPortrait ? 2.5 : 3.5, isPortrait ? 4 : 2);
      pdf.save(`${data.fullName || 'business-card'}.pdf`);
      toast.success('PDF with front & back downloaded');
    } catch (err) { toast.error('Export failed'); }
    finally { setIsExporting(false); }
  };

  const downloadVCard = () => {
    const vcard = `BEGIN:VCARD
VERSION:3.0
FN:${data.fullName}
TITLE:${data.title}
ORG:${data.company}
TEL:${data.phone}
EMAIL:${data.email}
URL:${data.website}
ADR:;;${data.location};;;
NOTE:${data.bio}
END:VCARD`;
    const blob = new Blob([vcard], { type: 'text/vcard' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${data.fullName || 'contact'}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('Contact saved');
  };

  const getQRUrl = () => {
    if (data.website) return data.website.startsWith('http') ? data.website : `https://${data.website}`;
    const slug = data.fullName?.toLowerCase().replace(/\s+/g, '-') || 'card';
    return `${window.location.origin}/card/${slug}`;
  };

  const getPublicUrl = () => {
    const slug = data.fullName?.toLowerCase().replace(/\s+/g, '-') || 'card';
    return `${window.location.origin}/card/${slug}`;
  };

  const copyPublicUrl = () => {
    navigator.clipboard.writeText(getPublicUrl());
    toast.success('Link copied');
  };

  const hasSocials = data.linkedin || data.instagram || data.twitter || data.tiktok || data.whatsapp;

  return (
    <div className="bcg-page">

      {/* ── Form panel ─────────────────────────────────────── */}
      <div className="bcg-form-pane">
        <div className="bcg-form-inner">
          <header className="bcg-header">
            <h1 className="bcg-title">Business card</h1>
            <p className="bcg-subtitle">Design your professional digital card.</p>
          </header>

          {/* Template */}
          <FormSection id="template" title="Template" expanded={expandedSections.template} onToggle={toggleSection}>
            <div className="bcg-tpl-grid">
              {TEMPLATES.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  className={`bcg-tpl${template === t.id ? ' is-active' : ''}`}
                  onClick={() => setTemplate(t.id)}
                >
                  <div className={`bcg-tpl-preview bcg-tpl-preview-${t.id}`} />
                  <span className="bcg-tpl-name">{t.name}</span>
                </button>
              ))}
            </div>
          </FormSection>

          {/* Accent color */}
          <FormSection id="color" title="Accent color" expanded={expandedSections.color} onToggle={toggleSection}>
            <div className="bcg-color-grid">
              {COLOR_PRESETS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={`bcg-color${accentColor === c.color ? ' is-active' : ''}`}
                  style={{ '--bcg-color': c.color }}
                  onClick={() => setAccentColor(c.color)}
                  aria-label={c.id}
                />
              ))}
              <label className="bcg-color-custom">
                <input
                  type="color"
                  value={accentColor}
                  onChange={(e) => setAccentColor(e.target.value)}
                />
                <span>Custom</span>
              </label>
            </div>
          </FormSection>

          {/* Layout */}
          <FormSection id="layout" title="Layout" expanded={expandedSections.layout} onToggle={toggleSection}>
            <div className="bcg-layout-grid">
              <button
                type="button"
                className={`bcg-layout${layout === 'landscape' ? ' is-active' : ''}`}
                onClick={() => setLayout('landscape')}
              >
                <span className="bcg-layout-icon bcg-layout-landscape" />
                <span>Landscape</span>
              </button>
              <button
                type="button"
                className={`bcg-layout${layout === 'portrait' ? ' is-active' : ''}`}
                onClick={() => setLayout('portrait')}
              >
                <span className="bcg-layout-icon bcg-layout-portrait" />
                <span>Portrait</span>
              </button>
            </div>
          </FormSection>

          {/* Basic */}
          <FormSection id="basic" title="Basic information" expanded={expandedSections.basic} onToggle={toggleSection}>
            <div className="bcg-grid">
              <div className="bcg-field bcg-field-full">
                <label>Full name *</label>
                <input
                  type="text"
                  value={data.fullName}
                  onChange={(e) => updateData('fullName', e.target.value)}
                  placeholder="John Smith"
                />
              </div>
              <div className="bcg-field">
                <label>Title / Role *</label>
                <input
                  type="text"
                  value={data.title}
                  onChange={(e) => updateData('title', e.target.value)}
                  placeholder="Creative Director"
                />
              </div>
              <div className="bcg-field">
                <label>Company</label>
                <input
                  type="text"
                  value={data.company}
                  onChange={(e) => updateData('company', e.target.value)}
                  placeholder="Acme Inc."
                />
              </div>
            </div>
          </FormSection>

          {/* Contact */}
          <FormSection id="contact" title="Contact" expanded={expandedSections.contact} onToggle={toggleSection}>
            <div className="bcg-grid">
              <div className="bcg-field">
                <label>Email *</label>
                <input
                  type="email"
                  value={data.email}
                  onChange={(e) => updateData('email', e.target.value)}
                  placeholder="john@example.com"
                />
              </div>
              <div className="bcg-field">
                <label>Phone</label>
                <input
                  type="tel"
                  value={data.phone}
                  onChange={(e) => updateData('phone', e.target.value)}
                  placeholder="+1 555 123 4567"
                />
              </div>
              <div className="bcg-field">
                <label>Website</label>
                <input
                  type="url"
                  value={data.website}
                  onChange={(e) => updateData('website', e.target.value)}
                  placeholder="https://example.com"
                />
              </div>
              <div className="bcg-field">
                <label>Location</label>
                <input
                  type="text"
                  value={data.location}
                  onChange={(e) => updateData('location', e.target.value)}
                  placeholder="Lagos, Nigeria"
                />
              </div>
            </div>
          </FormSection>

          {/* Profile */}
          <FormSection id="profile" title="Profile" expanded={expandedSections.profile} onToggle={toggleSection}>
            <div className="bcg-grid">
              <div className="bcg-field bcg-field-full">
                <label>
                  Short bio <span className="bcg-field-hint">({data.bio.length}/160)</span>
                </label>
                <textarea
                  value={data.bio}
                  onChange={(e) => updateData('bio', e.target.value.slice(0, 160))}
                  placeholder="A brief introduction about yourself…"
                  rows={3}
                />
              </div>
              <div className="bcg-field">
                <label>Headshot</label>
                {data.headshot ? (
                  <div className="bcg-headshot">
                    <img src={data.headshot} alt="Headshot" />
                    <button
                      type="button"
                      className="bcg-headshot-remove"
                      onClick={() => updateData('headshot', null)}
                      aria-label="Remove headshot"
                    >
                      <X size={14} /> Remove
                    </button>
                  </div>
                ) : (
                  <label className="bcg-upload">
                    <span className="bcg-upload-icon"><ImageIcon size={18} /></span>
                    <span className="bcg-upload-text">Upload headshot</span>
                    <input type="file" accept="image/*" onChange={handleHeadshotUpload} hidden />
                  </label>
                )}
              </div>
              <div className="bcg-field">
                <label>Business logo</label>
                {data.logo ? (
                  <div className="bcg-headshot bcg-headshot--logo">
                    <img src={data.logo} alt="Logo" />
                    <button
                      type="button"
                      className="bcg-headshot-remove"
                      onClick={() => updateData('logo', null)}
                      aria-label="Remove logo"
                    >
                      <X size={14} /> Remove
                    </button>
                  </div>
                ) : (
                  <label className="bcg-upload">
                    <span className="bcg-upload-icon"><Building2 size={18} /></span>
                    <span className="bcg-upload-text">Upload logo</span>
                    <input type="file" accept="image/*" onChange={handleLogoUpload} hidden />
                  </label>
                )}
              </div>
            </div>
          </FormSection>

          {/* Social */}
          <FormSection id="social" title="Social links" expanded={expandedSections.social} onToggle={toggleSection}>
            <div className="bcg-grid">
              <div className="bcg-field">
                <label>LinkedIn</label>
                <input
                  type="url"
                  value={data.linkedin}
                  onChange={(e) => updateData('linkedin', e.target.value)}
                  placeholder="linkedin.com/in/…"
                />
              </div>
              <div className="bcg-field">
                <label>Instagram</label>
                <input
                  type="text"
                  value={data.instagram}
                  onChange={(e) => updateData('instagram', e.target.value)}
                  placeholder="@username"
                />
              </div>
              <div className="bcg-field">
                <label>X (Twitter)</label>
                <input
                  type="text"
                  value={data.twitter}
                  onChange={(e) => updateData('twitter', e.target.value)}
                  placeholder="@username"
                />
              </div>
              <div className="bcg-field">
                <label>TikTok</label>
                <input
                  type="text"
                  value={data.tiktok}
                  onChange={(e) => updateData('tiktok', e.target.value)}
                  placeholder="@username"
                />
              </div>
              <div className="bcg-field bcg-field-full">
                <label>WhatsApp</label>
                <input
                  type="tel"
                  value={data.whatsapp}
                  onChange={(e) => updateData('whatsapp', e.target.value)}
                  placeholder="+1 555 123 4567"
                />
              </div>
            </div>
          </FormSection>

          {/* CTA */}
          <FormSection id="cta" title="Call to action" expanded={expandedSections.cta} onToggle={toggleSection}>
            <div className="bcg-grid">
              <div className="bcg-field">
                <label>Button label</label>
                <input
                  type="text"
                  value={data.ctaLabel}
                  onChange={(e) => updateData('ctaLabel', e.target.value)}
                  placeholder="Book a call"
                />
              </div>
              <div className="bcg-field">
                <label>Button link</label>
                <input
                  type="url"
                  value={data.ctaUrl}
                  onChange={(e) => updateData('ctaUrl', e.target.value)}
                  placeholder="https://calendly.com/…"
                />
              </div>
            </div>
          </FormSection>

          {/* QR toggle */}
          <section className="bcg-section">
            <label className="bcg-toggle-row">
              <span className="bcg-switch">
                <input
                  type="checkbox"
                  checked={showQR}
                  onChange={(e) => setShowQR(e.target.checked)}
                />
                <span className="bcg-switch-track" />
              </span>
              <span className="bcg-toggle-label">Show QR code on card</span>
            </label>
          </section>
        </div>
      </div>

      {/* ── Preview panel ──────────────────────────────────── */}
      <div className={`bcg-preview-pane${previewBg === 'dark' ? ' is-dark' : ''}`}>

        {/* Preview controls */}
        <div className="bcg-preview-toolbar">
          <div className="bcg-toggle-group">
            <button
              type="button"
              className={`bcg-toggle${previewMode === 'desktop' ? ' is-active' : ''}`}
              onClick={() => setPreviewMode('desktop')}
              aria-label="Desktop preview"
            >
              <Monitor size={14} />
              <span className="bcg-toggle-text">Desktop</span>
            </button>
            <button
              type="button"
              className={`bcg-toggle${previewMode === 'mobile' ? ' is-active' : ''}`}
              onClick={() => setPreviewMode('mobile')}
              aria-label="Mobile preview"
            >
              <Smartphone size={14} />
              <span className="bcg-toggle-text">Mobile</span>
            </button>
          </div>
          <div className="bcg-toggle-group">
            <button
              type="button"
              className={`bcg-toggle${previewBg === 'light' ? ' is-active' : ''}`}
              onClick={() => setPreviewBg('light')}
            >
              Light
            </button>
            <button
              type="button"
              className={`bcg-toggle${previewBg === 'dark' ? ' is-active' : ''}`}
              onClick={() => setPreviewBg('dark')}
            >
              Dark
            </button>
          </div>
        </div>

        {/* Card stage */}
        <div className={`bcg-stage${previewMode === 'mobile' ? ' is-mobile' : ''}`}>
          <div
            className={`bcg-flip${isFlipped ? ' is-flipped' : ''}`}
            onClick={() => setIsFlipped(!isFlipped)}
          >
            <div className="bcg-flip-inner">
              {/* Front — uses the existing .bcard* class system (preserved
                  intact from the original design; 81 rules across
                  templates / layouts / typography / social icons). */}
              <div className="bcg-flip-front">
                <div
                  ref={cardRef}
                  className={`bcard bcard--${template} bcard--${layout}`}
                  style={{ '--accent': accentColor }}
                >
                  {data.headshot && (
                    <div className="bcard__headshot">
                      <img src={data.headshot} alt="" />
                    </div>
                  )}

                  {data.logo && (
                    <div className="bcard__logo">
                      <img src={data.logo} alt="" />
                    </div>
                  )}

                  <div className="bcard__identity">
                    {data.fullName && <h2 className="bcard__name">{data.fullName}</h2>}
                    {data.title    && <p className="bcard__title">{data.title}</p>}
                    {data.company  && <p className="bcard__company">{data.company}</p>}
                  </div>

                  {data.bio && <p className="bcard__bio">{data.bio}</p>}

                  <div className="bcard__contact">
                    {data.email && (
                      <a
                        href={`mailto:${data.email}`}
                        className="bcard__contact-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Mail size={12} />
                        <span>{data.email}</span>
                      </a>
                    )}
                    {data.phone && (
                      <a
                        href={`tel:${data.phone}`}
                        className="bcard__contact-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Phone size={12} />
                        <span>{data.phone}</span>
                      </a>
                    )}
                    {data.website && (
                      <a
                        href={data.website}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bcard__contact-item"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <Globe size={12} />
                        <span>{data.website.replace(/^https?:\/\//, '')}</span>
                      </a>
                    )}
                    {data.location && (
                      <div className="bcard__contact-item">
                        <MapPin size={12} />
                        <span>{data.location}</span>
                      </div>
                    )}
                  </div>

                  {hasSocials && (
                    <div className="bcard__socials">
                      {data.linkedin && (
                        <a
                          href={data.linkedin.startsWith('http') ? data.linkedin : `https://linkedin.com/in/${data.linkedin}`}
                          target="_blank" rel="noopener noreferrer"
                          className="bcard__social" onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24"><path fill="currentColor" d={SOCIALS.find(s => s.id === 'linkedin').icon}/></svg>
                        </a>
                      )}
                      {data.instagram && (
                        <a
                          href={`https://instagram.com/${data.instagram.replace('@', '')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="bcard__social" onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24"><path fill="currentColor" d={SOCIALS.find(s => s.id === 'instagram').icon}/></svg>
                        </a>
                      )}
                      {data.twitter && (
                        <a
                          href={`https://x.com/${data.twitter.replace('@', '')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="bcard__social" onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24"><path fill="currentColor" d={SOCIALS.find(s => s.id === 'twitter').icon}/></svg>
                        </a>
                      )}
                      {data.tiktok && (
                        <a
                          href={`https://tiktok.com/@${data.tiktok.replace('@', '')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="bcard__social" onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24"><path fill="currentColor" d={SOCIALS.find(s => s.id === 'tiktok').icon}/></svg>
                        </a>
                      )}
                      {data.whatsapp && (
                        <a
                          href={`https://wa.me/${data.whatsapp.replace(/\D/g, '')}`}
                          target="_blank" rel="noopener noreferrer"
                          className="bcard__social" onClick={(e) => e.stopPropagation()}
                        >
                          <svg viewBox="0 0 24 24"><path fill="currentColor" d={SOCIALS.find(s => s.id === 'whatsapp').icon}/></svg>
                        </a>
                      )}
                    </div>
                  )}

                  {data.ctaLabel && data.ctaUrl && (
                    <a
                      href={data.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bcard__cta"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {data.ctaLabel}
                    </a>
                  )}

                  {showQR && (
                    <div className="bcard__qr">
                      <QRCodeSVG
                        value={getQRUrl()}
                        size={64}
                        fgColor={DARK_TEMPLATES.has(template) ? '#fff' : accentColor}
                        bgColor="transparent"
                        level="M"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Back */}
              <div className="bcg-flip-back">
                <div
                  ref={backRef}
                  className={`bcard bcard--${template} bcard--${layout} bcard-back`}
                  style={{ '--accent': accentColor }}
                >
                  <div className={`bcard-back__pattern bcard-back__pattern--${BACK_PATTERNS[template].type}`} />
                  {(data.logo || data.headshot) && !(layout === 'landscape' && showQR) && (
                    <div className="bcard-back__logo">
                      <img src={data.logo || data.headshot} alt="Logo" />
                    </div>
                  )}
                  {showQR && (
                    <div className="bcard-back__qr">
                      <QRCodeSVG
                        value={getQRUrl()}
                        size={80}
                        fgColor={DARK_TEMPLATES.has(template) ? '#fff' : accentColor}
                        bgColor="transparent"
                        level="M"
                      />
                    </div>
                  )}
                  {data.company && <p className="bcard-back__company">{data.company}</p>}
                </div>
              </div>
            </div>
          </div>

          <p className="bcg-flip-hint">
            <Sparkles size={11} /> {isFlipped ? 'Tap to see front' : 'Tap to see back (print only)'}
          </p>

          {/* Export actions */}
          <div className="bcg-actions">
            <button
              type="button"
              className="bcg-action bcg-action-primary"
              onClick={downloadPNG}
              disabled={isExporting}
            >
              <Download size={14} /> PNG
            </button>
            <button
              type="button"
              className="bcg-action"
              onClick={downloadPDF}
              disabled={isExporting}
            >
              <Download size={14} /> PDF
            </button>
            <button
              type="button"
              className="bcg-action"
              onClick={downloadVCard}
            >
              <FileText size={14} /> vCard
            </button>
            <button
              type="button"
              className="bcg-action"
              onClick={copyPublicUrl}
            >
              <Copy size={14} /> Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
